import { Component, Input, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Viol3ArbetsorderResponseDTO } from "~app/common/models/integration/viol3/Viol3ArbetsorderResponseDTO";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IXmlContainerResponse, XmlInformation } from "../models/IntegrationModel";
import { IntegrationService } from "../service/integration.service";

@Component({
    selector: "rask-viol3xml",
    templateUrl: "./viol3xml.component.html",
    styleUrls: ["./viol3xml.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TogglePanelComponent,
        TextareaComponent,
        FormsModule,
        SelectorComponent
    ]
})
export class Viol3xmlComponent {
    deliverySourceAckNackXml: string;
    deliverySourceInfoList: XmlInformation[];
    deliverySourceSelectedXmlInfo = { key: "",
        xml: "" };
    deliverySourceXmlSendResult: string;
    forestLoggingInformatioList: XmlInformation[];
    forestLoggingInformationAckNackXml: string;
    forestLoggingInformationSelectedXmlInfo = { key: "",
        xml: "" };
    forestloggingXmlSendResult: string;
    forstaledskontraktAckNackXml: string;
    forstaledskontraktSelectedXmlInfo = { key: "",
        xml: "" };
    forstaledskontraktXmlSendResult: string;
    loadTenderAckNackXml: string;
    loadTenderInfoList: XmlInformation[];
    loadTenderInfoXmlSendResult: string;
    loadTenderSelectedXmlInfo = { key: "",
        xml: "" };
    orderConfirmationAckNackXml: string;
    orderConfirmationSelectedXmlInfo = { key: "",
        xml: "" };
    orderConfirmationXmlInfoList: XmlInformation[];
    orderConfirmationXmlSendResult: string;
    @Input() selectedArbetsorder: Viol3ArbetsorderResponseDTO;

    private readonly busyService = inject(BusyService);
    private readonly integrationService = inject(IntegrationService);

    canGetXML() {
        return ObjectUtils.isNullOrUndefined(this.selectedArbetsorder);
    }

    formatter = (item: XmlInformation) => item.key;

    hamtaDeliverySourceAckNackXml() {
        this.integrationService.getViol3AckNackXml(this.deliverySourceSelectedXmlInfo.key, "DeliverySource").subscribe((res: IXmlContainerResponse) => {
            this.deliverySourceAckNackXml = res.xml;
        });
    }

    hamtaForestloggingAckNackXml() {
        this.integrationService.getViol3AckNackXml(this.forestLoggingInformationSelectedXmlInfo.key, "ForestLoggingInstruction").subscribe((res: IXmlContainerResponse) => {
            this.forestLoggingInformationAckNackXml = res.xml;
        });
    }

    hamtaForstaledskontrakAckNackXml() {
        this.integrationService.getViol3AckNackXml(this.forstaledskontraktSelectedXmlInfo.key, "OriginalContract").subscribe((res: IXmlContainerResponse) => {
            this.forstaledskontraktAckNackXml = res.xml;
        });
    }

    hamtaLoadTenderAckNackXml() {
        this.integrationService.getViol3AckNackXml(this.loadTenderSelectedXmlInfo.key, "LoadTender").subscribe((res: IXmlContainerResponse) => {
            this.loadTenderAckNackXml = res.xml;
        });
    }

    hamtaOrderConfirmationAckNackXml() {
        this.integrationService.getViol3AckNackXml(this.forestLoggingInformationSelectedXmlInfo.key, "OrderConfirmation").subscribe((res: IXmlContainerResponse) => {
            this.orderConfirmationAckNackXml = res.xml;
        });
    }

    hamtaViol3XML() {
        const getViol3XML = this.integrationService.getViol3Xml(this.selectedArbetsorder.beteckning);
        this.busyService.addWork(getViol3XML).subscribe((res: IXmlContainerResponse) => {
            this.forstaledskontraktSelectedXmlInfo = res.originalContractXml || {
                key: "",
                xml: ""
            };
            this.deliverySourceInfoList = res.deliverySourcesXml;
            if (res.deliverySourcesXml[0]) {
                this.deliverySourceSelectedXmlInfo = res.deliverySourcesXml[0];
            }
            this.forestLoggingInformatioList = res.forestLoggingInformationXml;
            if (res.forestLoggingInformationXml[0]) {
                this.forestLoggingInformationSelectedXmlInfo = res.forestLoggingInformationXml[0];
            }
            this.orderConfirmationXmlInfoList = res.orderConfirmationXml;
            if (res.orderConfirmationXml[0]) {
                this.orderConfirmationSelectedXmlInfo = res.orderConfirmationXml[0];
            }
            this.loadTenderInfoList = res.loadTenderXml;
            if(res.loadTenderXml[0]) {
                this.loadTenderSelectedXmlInfo = res.loadTenderXml[0];
            }
        });
    }

    outputFormatter = (item: XmlInformation) => item ? item.xml : undefined;

    skickaAvtalsobjektXml() {
        firstValueFrom(this.integrationService.sendXml(this.deliverySourceSelectedXmlInfo.xml, "DeliverySource")).then(x => {
            this.deliverySourceXmlSendResult = x.message;
        });
    }

    skickaDeliverySourceAckNackXml() {
        this.integrationService.sendAckNackXml(this.deliverySourceAckNackXml).subscribe((res: IXmlContainerResponse) => {
            // eslint-disable-next-line no-console
            console.log(res);
        });
    }

    skickaDestinerasortimentXml() {
        firstValueFrom(this.integrationService.sendXml(this.orderConfirmationSelectedXmlInfo.xml, "OrderConfirmation")).then(x => {
            this.orderConfirmationXmlSendResult = x.message;
        });
    }

    skickaForestLoggingInstructionAckNackXml() {
        this.integrationService.sendAckNackXml(this.forestLoggingInformationAckNackXml).subscribe((res: IXmlContainerResponse) => {
            // eslint-disable-next-line no-console
            console.log(res);
        });
    }

    skickaForstaledskontrakAckNackXml() {
        this.integrationService.sendAckNackXml(this.forstaledskontraktAckNackXml).subscribe((res: IXmlContainerResponse) => {
            // eslint-disable-next-line no-console
            console.log(res);
        });
    }

    skickaForstaledskontrakXml() {
        firstValueFrom(this.integrationService.sendXml(this.forstaledskontraktSelectedXmlInfo.xml, "OriginalContract")).then(x => {
            this.forstaledskontraktXmlSendResult = x.message;
        });
    }

    skickaLoadTenderAckNackXml() {
        this.integrationService.sendAckNackXml(this.loadTenderAckNackXml).subscribe((res: IXmlContainerResponse) => {
            // eslint-disable-next-line no-console
            console.log(res);
        });
    }

    skickaOrderConfirmationAckNackXml() {
        this.integrationService.sendAckNackXml(this.orderConfirmationAckNackXml).subscribe((res: IXmlContainerResponse) => {
            // eslint-disable-next-line no-console
            console.log(res);
        });
    }

    skickaProduktionsunderlagXml() {
        firstValueFrom(this.integrationService.sendXml(this.forestLoggingInformationSelectedXmlInfo.xml, "ForestLoggingInstruction")).then(x => {
            this.forestloggingXmlSendResult = x.message;
        });
    }

    skickaTransportunderlagXml() {
        firstValueFrom(this.integrationService.sendXml(this.loadTenderSelectedXmlInfo.xml, "LoadTender")).then(x => {
            this.loadTenderInfoXmlSendResult = x.message;
        });
    }
}
